import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import type { MyLocationOption } from './contactMyLocationFields';

function parseCountryOptionsFromResponse(response: unknown): MyLocationOption[] {
    const codes = (response as any)?.codes || (response as any)?.data?.codes || [];
    const mapped: MyLocationOption[] = codes
        .map((code: any) => ({
            TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
            // 콤보 표시용 — Country 라벨 전부 대문자 (Malaysia → MALAYSIA)
            CODE_NAME: String(
                code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || ''
            )
                .trim()
                .toUpperCase(),
        }))
        .filter(
            (item: MyLocationOption) =>
                item.TOTAL_CODE && item.CODE_NAME && item.TOTAL_CODE.length > 0 && item.CODE_NAME.length > 0
        );

    const uniqueCodesMap = new Map<string, MyLocationOption>();
    mapped.forEach((item: MyLocationOption) => {
        if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
            uniqueCodesMap.set(item.TOTAL_CODE, item);
        }
    });

    return Array.from(uniqueCodesMap.values()).filter((item: MyLocationOption) => {
        const subCode = item.TOTAL_CODE.slice(-3);
        return subCode !== '000';
    });
}

function parseStateOptionsFromResponse(response: unknown): MyLocationOption[] {
    const states = (response as any)?.data?.data || (response as any)?.data || [];
    return (Array.isArray(states) ? states : [])
        .map((state: any) => ({
            TOTAL_CODE: String(state.code || '').trim(),
            CODE_NAME: String(state.name || '').trim(),
        }))
        .filter((item: MyLocationOption) => item.TOTAL_CODE && item.CODE_NAME);
}

function parseAllPostcodeOptionsFromRows(postcodes: any[]): MyLocationOption[] {
    const uniquePostcodesMap = new Map<string, MyLocationOption>();
    postcodes.forEach((postcode: any) => {
        const codeName = String(postcode.name || '').trim();
        const totalCode = String(postcode.code || postcode.name || '').trim();
        if (codeName && totalCode && !uniquePostcodesMap.has(codeName)) {
            uniquePostcodesMap.set(codeName, {
                TOTAL_CODE: totalCode,
                CODE_NAME: codeName,
            });
        }
    });
    return Array.from(uniquePostcodesMap.values());
}

let countryCache: MyLocationOption[] | null = null;
let countryInflight: Promise<MyLocationOption[]> | null = null;

let stateCache: MyLocationOption[] | null = null;
let stateInflight: Promise<MyLocationOption[]> | null = null;

let allPostcodeCache: MyLocationOption[] | null = null;
let allPostcodeRawCache: any[] | null = null;
let allPostcodeInflight: Promise<MyLocationOption[]> | null = null;

/** B15 Country — 세션당 1회 */
export async function fetchCachedMyCountryOptions(): Promise<MyLocationOption[]> {
    if (countryCache && countryCache.length > 0) {
        return countryCache;
    }
    if (countryInflight) {
        return countryInflight;
    }
    countryInflight = (async () => {
        try {
            const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
            countryCache = parseCountryOptionsFromResponse(response);
            return countryCache;
        } catch {
            countryCache = [];
            return [];
        } finally {
            countryInflight = null;
        }
    })();
    return countryInflight;
}

/** Malaysia States — 세션당 1회 */
export async function fetchCachedMyStateOptions(): Promise<MyLocationOption[]> {
    if (stateCache && stateCache.length > 0) {
        return stateCache;
    }
    if (stateInflight) {
        return stateInflight;
    }
    stateInflight = (async () => {
        try {
            const response = await entityMyApi.getMalaysiaStates();
            stateCache = parseStateOptionsFromResponse(response);
            return stateCache;
        } catch {
            stateCache = [];
            return [];
        } finally {
            stateInflight = null;
        }
    })();
    return stateInflight;
}

/** 전국 Postcode — 세션당 1회 (역검색·Postcode 콤보용) */
export async function fetchCachedMyAllPostcodeOptions(): Promise<MyLocationOption[]> {
    if (allPostcodeCache && allPostcodeCache.length > 0) {
        return allPostcodeCache;
    }
    if (allPostcodeInflight) {
        return allPostcodeInflight;
    }
    allPostcodeInflight = (async () => {
        try {
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('전체 Postcode 목록 로드 타임아웃')), 10000)
            );
            const response = (await Promise.race([
                entityMyApi.getAllMalaysiaPostcodes(),
                timeoutPromise,
            ])) as any;
            const postcodes = response?.data?.data || response?.data || [];
            allPostcodeRawCache = Array.isArray(postcodes) ? postcodes : [];
            allPostcodeCache = parseAllPostcodeOptionsFromRows(allPostcodeRawCache);
            return allPostcodeCache;
        } catch {
            allPostcodeRawCache = [];
            allPostcodeCache = [];
            return [];
        } finally {
            allPostcodeInflight = null;
        }
    })();
    return allPostcodeInflight;
}

/** ContactInfoForm postcode lookup merge용 원본 행 */
export function getCachedMyAllPostcodeRawRows(): any[] {
    return allPostcodeRawCache || [];
}

export async function ensureCachedMyCountryAndState(): Promise<{
    countries: MyLocationOption[];
    states: MyLocationOption[];
}> {
    const [countries, states] = await Promise.all([
        fetchCachedMyCountryOptions(),
        fetchCachedMyStateOptions(),
    ]);
    return { countries, states };
}
